import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import InfoTooltip from '../../../components/ui/Tooltip';
import { useCurrency } from '../../../contexts/CurrencyContext';

/**
 * SimplifiedOverview - Vue d'ensemble avec:
 * - Score /100
 * - Indicateurs de rendement avec barre de niveau 0-5 (5 = excellent)
 * - Indicateurs de risque avec barre de niveau 0-5 (0 = peu risqué, 5 = très risqué)
 * - Forces et faiblesses
 * - Graphique Recharts 10 ans
 */
const SimplifiedOverview = ({ property, kpis, projectionData = [] }) => {
  const { formatCurrency } = useCurrency();

  const projectionYears = (projectionData && projectionData.length > 1) ? projectionData[projectionData.length - 1].year : 30;

  // Calcul du ROI total moyen (CAGR)
  const avgAnnualROI = useMemo(() => {
    if (!projectionData || projectionData.length < 2 || !property) return 0;
    const finalYear = projectionData[projectionData.length - 1];
    const initialInvestment = property?.total_initial_investment || property?.down_payment_amount || 0;
    if (initialInvestment <= 0) return 0;

    const cashflowCumulative = projectionData.reduce((sum, y) => sum + (parseFloat(y?.annualCashflow) || 0), 0);
    const valueAppreciation = (finalYear?.propertyValue || 0) - (property?.purchase_price || 0);
    const principalPaydown = (property?.mortgage_amount || 0) - (finalYear?.mortgageBalance || 0);
    const totalROIAmount = cashflowCumulative + valueAppreciation + principalPaydown;
    const totalROIDecimal = totalROIAmount / initialInvestment;
    const n = finalYear?.year || projectionYears;

    if (n <= 0 || (1 + totalROIDecimal) <= 0) return 0;
    return (Math.pow(1 + totalROIDecimal, 1 / n) - 1) * 100;
  }, [projectionData, property, projectionYears]);

  // Calcul du score /100 et niveaux 0-5
  const { score, performanceLevel, riskLevel } = useMemo(() => {
    if (!property || !kpis) return { score: 50, performanceLevel: 3, riskLevel: 3 };

    let points = 0;
    let maxPoints = 0;
    let perfPoints = 0;
    let riskScore = 0; // Plus c'est haut, plus c'est risqué

    // ========== RENDEMENT (contribue au score et performanceLevel) ==========
    // Cashflow (25 points max)
    const cashflow = kpis.monthlyCashflow || 0;
    maxPoints += 25;
    if (cashflow >= 500) { points += 25; perfPoints += 2; }
    else if (cashflow >= 300) { points += 20; perfPoints += 1.5; }
    else if (cashflow >= 100) { points += 15; perfPoints += 1; }
    else if (cashflow >= 0) { points += 10; perfPoints += 0.5; }
    else if (cashflow >= -200) { points += 5; riskScore += 0.5; }
    else { riskScore += 1; }

    // Cash-on-Cash (25 points max)
    const coc = kpis.cashOnCashReturn || 0;
    maxPoints += 25;
    if (coc >= 12) { points += 25; perfPoints += 2; }
    else if (coc >= 10) { points += 22; perfPoints += 1.5; }
    else if (coc >= 8) { points += 18; perfPoints += 1; }
    else if (coc >= 6) { points += 14; perfPoints += 0.5; }
    else if (coc >= 4) { points += 10; }
    else if (coc >= 2) { points += 5; riskScore += 0.25; }
    else { riskScore += 0.5; }

    // Cap Rate (20 points max)
    const capRate = kpis.capRate || 0;
    maxPoints += 20;
    if (capRate >= 8) { points += 20; perfPoints += 1; }
    else if (capRate >= 6) { points += 16; perfPoints += 0.75; }
    else if (capRate >= 5) { points += 12; perfPoints += 0.5; }
    else if (capRate >= 4) { points += 8; perfPoints += 0.25; }
    else if (capRate >= 3) { points += 4; riskScore += 0.25; }
    else { riskScore += 0.5; }

    // ========== RISQUE (0 = peu risqué, 5 = très risqué) ==========
    // DSCR - Plus c'est bas, plus c'est risqué
    const dscr = kpis.dscr || 0;
    maxPoints += 20;
    if (dscr >= 1.5) { points += 20; }
    else if (dscr >= 1.3) { points += 16; riskScore += 0.5; }
    else if (dscr >= 1.2) { points += 12; riskScore += 1; }
    else if (dscr >= 1.1) { points += 8; riskScore += 1.5; }
    else if (dscr >= 1.0) { points += 4; riskScore += 2; }
    else { riskScore += 2.5; }

    // Expense ratio - Plus c'est haut, plus c'est risqué
    const expenseRatio = kpis.expenseRatio || 50;
    maxPoints += 10;
    if (expenseRatio <= 35) { points += 10; }
    else if (expenseRatio <= 45) { points += 8; riskScore += 0.25; }
    else if (expenseRatio <= 55) { points += 5; riskScore += 0.5; }
    else if (expenseRatio <= 65) { points += 2; riskScore += 0.75; }
    else { riskScore += 1; }

    // Diversification - Moins d'unités = plus risqué
    const units = property.units || 1;
    if (units === 1) { riskScore += 0.5; }
    else if (units <= 2) { riskScore += 0.25; }
    // Plus d'unités = pas de risque additionnel

    // Normalize levels
    const perfLevel = Math.min(5, Math.round(perfPoints));
    // Risk: cap at 5, round
    const rskLevel = Math.min(5, Math.round(riskScore));

    return {
      score: Math.round((points / maxPoints) * 100),
      performanceLevel: perfLevel,
      riskLevel: rskLevel
    };
  }, [property, kpis]);

  // Analyse des forces et faiblesses
  const analysis = useMemo(() => {
    if (!property || !kpis) return { strengths: [], weaknesses: [] };

    const strengths = [];
    const weaknesses = [];
    const currentYear = new Date().getFullYear();

    // Âge de l'immeuble
    const yearBuilt = property.year_built;
    if (yearBuilt) {
      const age = currentYear - yearBuilt;
      if (age <= 15) {
        strengths.push({ label: 'Construction récente', value: `${age} ans` });
      } else if (age >= 50) {
        weaknesses.push({ label: 'Immeuble ancien', value: `${age} ans` });
      } else if (age >= 35) {
        weaknesses.push({ label: 'Rénovations à prévoir', value: `${age} ans` });
      }
    }

    // Prix par pied carré
    const squareFeet = property.square_feet;
    const purchasePrice = property.purchase_price;
    if (squareFeet && purchasePrice && squareFeet > 0) {
      const pricePerSqFt = purchasePrice / squareFeet;
      if (pricePerSqFt <= 150) {
        strengths.push({ label: 'Prix/pi² avantageux', value: formatCurrency(pricePerSqFt) + '/pi²' });
      } else if (pricePerSqFt >= 300) {
        weaknesses.push({ label: 'Prix/pi² élevé', value: formatCurrency(pricePerSqFt) + '/pi²' });
      }
    }

    // Type et diversification
    const propertyType = property.property_type?.toLowerCase() || '';
    const units = property.units || 1;
    if (propertyType.includes('commercial') || propertyType.includes('mixte')) {
      weaknesses.push({ label: 'Gestion complexe', value: 'Commercial/Mixte' });
    } else if (units >= 2 && units <= 4) {
      strengths.push({ label: 'Petit plex facile', value: `${units} unités` });
    }

    if (units >= 6) {
      strengths.push({ label: 'Excellente diversification', value: `${units} unités` });
    } else if (units === 1) {
      weaknesses.push({ label: 'Risque concentré', value: '1 unité' });
    }

    // Marge de sécurité
    const monthlyRent = property.monthly_rent || property.total_monthly_revenue || 0;
    const monthlyCashflow = kpis.monthlyCashflow || 0;
    if (monthlyRent > 0) {
      const bufferPercent = (monthlyCashflow / monthlyRent) * 100;
      if (bufferPercent >= 20) {
        strengths.push({ label: 'Marge sécuritaire', value: `${bufferPercent.toFixed(0)}%` });
      } else if (bufferPercent < 5 && bufferPercent >= 0) {
        weaknesses.push({ label: 'Marge très serrée', value: `${bufferPercent.toFixed(0)}%` });
      }
    }

    // Vacance
    const vacancyRate = property.vacancy_rate || 5;
    if (vacancyRate <= 2) {
      strengths.push({ label: 'Vacance très faible', value: `${vacancyRate}%` });
    } else if (vacancyRate >= 10) {
      weaknesses.push({ label: 'Vacance élevée', value: `${vacancyRate}%` });
    }

    // Assurer minimum 2 de chaque
    if (strengths.length < 2) {
      if (kpis.monthlyCashflow > 0) {
        strengths.push({ label: 'Cashflow positif', value: formatCurrency(kpis.monthlyCashflow) + '/mois' });
      }
      if (kpis.cashOnCashReturn >= 6 && strengths.length < 2) {
        strengths.push({ label: 'Bon rendement', value: kpis.cashOnCashReturn.toFixed(1) + '%' });
      }
    }

    if (weaknesses.length < 2 && weaknesses.length > 0) {
      if (kpis.expenseRatio > 50) {
        weaknesses.push({ label: 'Dépenses à surveiller', value: kpis.expenseRatio.toFixed(0) + '%' });
      }
    }

    return {
      strengths: strengths.slice(0, 4),
      weaknesses: weaknesses.slice(0, 4)
    };
  }, [property, kpis, formatCurrency]);

  // Données pour le graphique - maintenant annualCashflow est cohérent pour toutes les années
  const chartData = useMemo(() => {
    if (!projectionData || projectionData.length === 0) return null;

    return projectionData.map((d, i) => ({
      year: i,
      propertyValue: d.propertyValue || 0,
      mortgageBalance: d.mortgageBalance || 0,
      equity: d.equity || Math.max(0, (d.propertyValue || 0) - (d.mortgageBalance || 0)),
      cashflow: d.annualCashflow || 0
    }));
  }, [projectionData]);

  // Résumé année 0 et dernière année
  const projectionSummary = useMemo(() => {
    if (!projectionData || projectionData.length === 0) return null;
    const year0 = projectionData[0];
    const yearFinal = projectionData[projectionData.length - 1];

    return {
      year0: {
        value: year0?.propertyValue || 0,
        mortgage: year0?.mortgageBalance || 0,
        cashflow: year0?.annualCashflow || 0
      },
      yearFinal: {
        year: yearFinal?.year || projectionYears,
        value: yearFinal?.propertyValue || 0,
        mortgage: yearFinal?.mortgageBalance || 0,
        cashflow: yearFinal?.annualCashflow || 0
      }
    };
  }, [projectionData, projectionYears]);

  const getScoreColor = (s) => s >= 70 ? '#16a34a' : s >= 50 ? '#f59e0b' : '#ef4444';
  const getScoreLabel = (s) => {
    if (s >= 80) return 'Excellent';
    if (s >= 70) return 'Très bon';
    if (s >= 60) return 'Bon';
    if (s >= 50) return 'Correct';
    if (s >= 40) return 'Passable';
    return 'À améliorer';
  };

  // Couleur du risque: 0 = vert (peu risqué), 5 = rouge (très risqué)
  const getRiskColor = (level) => {
    if (level <= 1) return '#16a34a'; // Vert
    if (level <= 2) return '#84cc16'; // Vert clair
    if (level <= 3) return '#f59e0b'; // Orange
    if (level <= 4) return '#f97316'; // Orange foncé
    return '#ef4444'; // Rouge
  };

  const getPerformanceColor = (level) => {
    if (level >= 5) return '#16a34a'; // Vert
    if (level >= 4) return '#84cc16'; // Vert clair
    if (level >= 3) return '#fcd34d'; // Jaune
    if (level >= 2) return '#fb923c'; // Orange
    if (level >= 1) return '#f97316'; // Orange foncé
    return '#ef4444'; // Rouge
  };

  const getRiskLabel = (level) => {
    if (level <= 1) return 'Très faible';
    if (level <= 2) return 'Faible';
    if (level <= 3) return 'Modéré';
    if (level <= 4) return 'Élevé';
    return 'Très élevé';
  };

  // Barre de niveau
  const LevelBar = ({ level, maxLevel = 5, color, inverted = false }) => (
    <div style={{ display: 'flex', gap: '4px', marginTop: '8px' }}>
      {Array.from({ length: maxLevel }, (_, i) => {
        const filled = inverted ? i < level : i < level;
        return (
          <div
            key={i}
            style={{
              flex: 1,
              height: '8px',
              borderRadius: '4px',
              background: filled ? color : 'var(--color-muted)',
              transition: 'background 0.3s ease'
            }}
          />
        );
      })}
    </div>
  );

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          background: 'var(--color-card)',
          border: '1px solid var(--color-border)',
          borderRadius: '8px',
          padding: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}>
          <p style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: '8px' }}>Année {label}</p>
          {payload.map((entry, index) => (
            <div key={index} style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', fontSize: '0.75rem', marginBottom: '4px' }}>
              <span style={{ color: entry.color }}>{entry.name}:</span>
              <span style={{ fontWeight: 600 }}>{formatCurrency(entry.value)}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Ligne 1: Score + Rendement + Risque */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Score /100 */}
        <div className="bg-card border border-border rounded-xl p-6 flex flex-col items-center justify-center min-w-[120px]">
          <div className="w-[72px] h-[72px] rounded-full flex flex-col items-center justify-center mb-2" style={{ border: `4px solid ${getScoreColor(score)}` }}>
            <span className="text-2xl font-bold leading-none" style={{ color: getScoreColor(score) }}>
              {score}
            </span>
            <span className="text-[0.625rem] text-muted-foreground">/100</span>
          </div>
          <span className="text-[0.8125rem] font-semibold" style={{ color: getScoreColor(score) }}>
            {getScoreLabel(score)}
          </span>
        </div>

        {/* Rendement (5 = excellent) */}
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <Icon name="TrendingUp" size={18} color={getPerformanceColor(performanceLevel)} />
              <h3 className="text-[0.9375rem] font-semibold">Rendement</h3>
              <InfoTooltip content="Analyse de la rentabilité globale basée sur le cashflow généré et le retour sur capital investi." />
            </div>
            <span className="text-xs font-semibold" style={{ color: getPerformanceColor(performanceLevel) }}>{performanceLevel}/5</span>
          </div>
          <LevelBar level={performanceLevel} color={getPerformanceColor(performanceLevel)} />
          <div className="mt-4 flex flex-col gap-1">
            <div className="flex justify-between text-xs items-center">
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground">Cashflow</span>
                <InfoTooltip content="Le profit net restant chaque mois après avoir payé toutes les dépenses et l'hypothèque." />
              </div>
              <span className="font-semibold" style={{ color: (kpis?.monthlyCashflow || 0) >= 0 ? '#16a34a' : '#ef4444' }}>
                {formatCurrency(kpis?.monthlyCashflow || 0)}/mois
              </span>
            </div>
            <div className="flex justify-between text-xs items-center">
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground">ROI Cash-on-Cash</span>
                <InfoTooltip content="Le rendement annuel sur l'argent que vous avez réellement investi (mise de fonds, frais, rénos)." />
              </div>
              <span className="font-semibold">{(kpis?.cashOnCashReturn || 0).toFixed(1)}%</span>
            </div>
            <div className="flex justify-between text-xs items-center">
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground">ROI total moyen / an</span>
                <InfoTooltip content="Taux de rendement annuel composé (CAGR) incluant le cashflow, la prise de valeur et le remboursement du prêt." />
              </div>
              <span className="font-semibold">{avgAnnualROI.toFixed(1)}%</span>
            </div>
          </div>
        </div>

        {/* Risque (0 = peu risqué, 5 = très risqué) */}
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <Icon name="Shield" size={18} color={getRiskColor(riskLevel)} />
              <h3 className="text-[0.9375rem] font-semibold">Risque</h3>
              <InfoTooltip content="Indice de risque basé sur l'âge de l'immeuble, le quartier et la stabilité des revenus." />
            </div>
            <span
              className="text-xs font-semibold px-2 py-0.5 rounded-full"
              style={{
                color: getRiskColor(riskLevel),
                background: `${getRiskColor(riskLevel)}15`
              }}
            >
              {getRiskLabel(riskLevel)}
            </span>
          </div>
          <LevelBar level={riskLevel} color={getRiskColor(riskLevel)} />
          <p className="text-[0.625rem] text-muted-foreground mt-1 text-right">
            0 = peu risqué → 5 = très risqué
          </p>
          <div className="mt-2 flex flex-col gap-1">
            <div className="flex justify-between text-xs items-center">
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground">Ratio couverture dette</span>
                <InfoTooltip content="Le DSCR mesure la capacité de l'immeuble à payer son hypothèque. Un ratio de 1.25 signifie que l'immeuble génère 25% de plus que le paiement hypothécaire." />
              </div>
              <span className="font-semibold">{(kpis?.dscr || 0).toFixed(2)}x</span>
            </div>
            <div className="flex justify-between text-xs items-center">
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground">Ratio dépenses/revenus</span>
                <InfoTooltip content="Pourcentage des revenus bruts utilisé pour payer les dépenses. Moins ce ratio est élevé, plus l'immeuble est efficace." />
              </div>
              <span className="font-semibold">{(kpis?.expenseRatio || 0).toFixed(0)}%</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Taux de capitalisation</span>
              <span className="font-semibold">{(kpis?.capRate || 0).toFixed(1)}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Ligne 2: Forces et Faiblesses */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Forces */}
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 rounded-lg bg-[#16a34a]/10 flex items-center justify-center">
              <Icon name="ThumbsUp" size={16} color="#16a34a" />
            </div>
            <h3 className="text-[0.9375rem] font-semibold">Forces</h3>
          </div>
          <div className="flex flex-col gap-1.5">
            {analysis.strengths.length > 0 ? analysis.strengths.map((item, i) => (
              <div key={i} className="flex justify-between items-center p-2 md:px-3 bg-[#16a34a]/5 rounded-lg border-l-4 border-l-[#16a34a]">
                <span className="text-[0.8125rem]">{item.label}</span>
                <span className="text-[0.8125rem] font-semibold text-[#16a34a]">{item.value}</span>
              </div>
            )) : (
              <p className="text-[0.8125rem] text-muted-foreground italic">
                Analyse en cours...
              </p>
            )}
          </div>
        </div>

        {/* Faiblesses */}
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 rounded-lg bg-[#ef4444]/10 flex items-center justify-center">
              <Icon name="AlertTriangle" size={16} color="#ef4444" />
            </div>
            <h3 className="text-[0.9375rem] font-semibold">Faiblesses</h3>
          </div>
          <div className="flex flex-col gap-1.5">
            {analysis.weaknesses.length > 0 ? analysis.weaknesses.map((item, i) => (
              <div key={i} className="flex justify-between items-center p-2 md:px-3 bg-[#ef4444]/5 rounded-lg border-l-4 border-l-[#ef4444]">
                <span className="text-[0.8125rem]">{item.label}</span>
                <span className="text-[0.8125rem] font-semibold text-[#ef4444]">{item.value}</span>
              </div>
            )) : (
              <div className="flex items-center gap-1.5 p-2 md:px-3 bg-[#16a34a]/5 rounded-lg">
                <Icon name="Check" size={14} color="#16a34a" />
                <span className="text-[0.8125rem] text-[#16a34a]">Aucune faiblesse majeure</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Ligne 3: Graphique Interactif */}
      {chartData && projectionSummary && (
        <InteractiveChart
          chartData={chartData}
          projectionSummary={projectionSummary}
          formatCurrency={formatCurrency}
          projectionYears={projectionYears}
        />
      )}
    </div>
  );
};

// Composant interne pour le graphique interactif
const InteractiveChart = ({ chartData, projectionSummary, formatCurrency, projectionYears = 30 }) => {
  const [metric, setMetric] = React.useState('global');

  const metrics = [
    { id: 'global', label: 'Vue globale', icon: 'Activity' },
    { id: 'cashflow', label: 'Cashflow', icon: 'DollarSign' },
    { id: 'equity', label: 'Équité', icon: 'PieChart' }
  ];

  const getChartLines = () => {
    switch (metric) {
      case 'cashflow':
        return [
          <Line key="cf" yAxisId="left" type="monotone" dataKey="cashflow" name="Cashflow" stroke="#22c55e" strokeWidth={3} dot={false} activeDot={{ r: 6 }} />
        ];
      case 'global':
        return [
          <Line key="eq" yAxisId="left" type="monotone" dataKey="equity" name="Équité Nette" stroke="#16a34a" strokeWidth={3} dot={false} activeDot={{ r: 6 }} />,
          <Line key="val" yAxisId="left" type="monotone" dataKey="propertyValue" name="Valeur" stroke="#3b82f6" strokeWidth={2} strokeDasharray="5 5" dot={false} />,
          <Line key="mtg" yAxisId="left" type="monotone" dataKey="mortgageBalance" name="Hypothèque" stroke="#ef4444" strokeWidth={2} strokeDasharray="5 5" dot={false} />
        ];
      case 'equity':
      default:
        return [
          <Line key="val" yAxisId="left" type="monotone" dataKey="propertyValue" name="Valeur" stroke="#3b82f6" strokeWidth={3} dot={false} activeDot={{ r: 6 }} />,
          <Line key="mtg" yAxisId="left" type="monotone" dataKey="mortgageBalance" name="Hypothèque" stroke="#ef4444" strokeWidth={3} dot={false} activeDot={{ r: 6 }} />
        ];
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-4 md:p-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Icon name="LineChart" size={18} className="text-primary" />
          <h3 className="text-lg font-semibold">Projection sur {projectionYears} ans</h3>
        </div>

        <div className="flex p-1 bg-muted rounded-lg overflow-x-auto scrollbar-hide">
          {metrics.map(m => (
            <button
              key={m.id}
              onClick={() => setMetric(m.id)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-all ${metric === m.id
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
                }`}
            >
              <Icon name={m.icon} size={14} />
              <span>{m.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Résumé Année 0 */}
        <div className="lg:w-32 grid grid-cols-3 lg:grid-cols-1 gap-2 lg:gap-3 p-3 bg-muted rounded-lg self-start">
          <div className="col-span-3 lg:col-span-1">
            <p className="text-sm font-semibold">Année 0</p>
          </div>
          <div>
            <p className="text-[0.625rem] text-muted-foreground">Valeur</p>
            <p className="text-sm font-semibold text-blue-500">{formatCurrency(projectionSummary.year0.value)}</p>
          </div>
          <div>
            <p className="text-[0.625rem] text-muted-foreground">Hypothèque</p>
            <p className="text-sm font-semibold text-red-500">{formatCurrency(projectionSummary.year0.mortgage)}</p>
          </div>
          <div>
            <p className="text-[0.625rem] text-muted-foreground">Cashflow</p>
            <p className="text-sm font-semibold text-green-500">{formatCurrency(projectionSummary.year0.cashflow)}</p>
          </div>
        </div>

        {/* Graphique */}
        <div className="w-full" style={{ height: '280px', minHeight: '280px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
              <XAxis
                dataKey="year"
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '0.75rem' }}
                tickFormatter={(v) => `An ${v}`}
                tickMargin={10}
              />
              <YAxis
                yAxisId="left"
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '0.75rem' }}
                tickFormatter={(v) => `$${Math.round(v / 1000)}k`}
                width={40}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    return (
                      <div className="bg-card border border-border rounded-lg shadow-lg p-3 text-xs">
                        <p className="font-semibold mb-2">Année {label}</p>
                        {payload.map((entry, i) => (
                          <div key={i} className="flex items-center justify-between gap-4 mb-1">
                            <span style={{ color: entry.color }}>{entry.name}:</span>
                            <span className="font-semibold">{formatCurrency(entry.value)}</span>
                          </div>
                        ))}
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Legend wrapperStyle={{ fontSize: '0.75rem', paddingTop: '10px' }} />
              {getChartLines()}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Résumé dernière année */}
        <div className="lg:w-32 grid grid-cols-3 lg:grid-cols-1 gap-2 lg:gap-3 p-3 bg-muted rounded-lg self-start">
          <div className="col-span-3 lg:col-span-1">
            <p className="text-sm font-semibold">Année {projectionSummary.yearFinal.year}</p>
          </div>
          <div>
            <p className="text-[0.625rem] text-muted-foreground">Valeur</p>
            <p className="text-sm font-semibold text-blue-500">{formatCurrency(projectionSummary.yearFinal.value)}</p>
          </div>
          <div>
            <p className="text-[0.625rem] text-muted-foreground">Hypothèque</p>
            <p className="text-sm font-semibold text-red-500">{formatCurrency(projectionSummary.yearFinal.mortgage)}</p>
          </div>
          <div>
            <p className="text-[0.625rem] text-muted-foreground">Cashflow</p>
            <p className="text-sm font-semibold text-green-500">{formatCurrency(projectionSummary.yearFinal.cashflow)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimplifiedOverview;
